import { useNavigate } from "../lib/router-context";
import { trackEvent } from "../lib/analytics";
import type { ThemeColors } from "../lib/theme";
import { SvgIcon } from "./ui/SvgIcon";
import chevronRightIcon from "../assets/chevron-right-icon.svg?raw";

type OpenLobbyCardProps = {
  lobbyId: string;
  startTitle: string;
  targetTitle: string;
  phase: string;
  gameRemaining: number | null;
  scoreCountdown: number | null;
  isDark: boolean;
  theme: ThemeColors;
  hasPlayed?: boolean;
  playerCount?: number;
};

export function OpenLobbyCard({
  lobbyId,
  startTitle,
  targetTitle,
  phase,
  gameRemaining,
  scoreCountdown,
  isDark,
  theme,
  hasPlayed = false,
  playerCount,
}: OpenLobbyCardProps) {
  const navigate = useNavigate();
  const disabled = phase !== "Game" || hasPlayed;
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <button
      onClick={() => {
        if (disabled) return;
        trackEvent("openlobby-player-start", { lobbyId });
        navigate(`/openlobby/${lobbyId}/game`);
      }}
      disabled={disabled}
      className={`group w-full text-left cursor-pointer flex flex-col ${theme.card} rounded-xl p-6 ${theme.cardBorder} transition-all duration-300 ${disabled ? "opacity-60 cursor-not-allowed" : theme.cardHover}`}
    >
      <div className="flex w-full items-center gap-3 mb-4">
        <div
          className={`w-8 h-8 rounded-lg ${isDark ? "bg-zinc-800" : "bg-gray-100"} flex items-center justify-center shrink-0`}
        >
          <svg
            className={`w-4 h-4 ${theme.text}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        </div>
        <h3 className={`${theme.text} font-medium text-lg flex-1`}>
          Open Lobby
        </h3>
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${isDark ? "bg-zinc-800 text-zinc-400" : "bg-gray-100 text-gray-600"}`}
        >
          <span
            className={`w-2 h-2 rounded-full border border-ink ${phase === "Game" ? "bg-mint board-pulse" : "bg-ink/40"}`}
          />
          {phase === "Game" ? "Live" : "Game Over"}
        </span>
        {playerCount != null && playerCount > 0 && (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${isDark ? "bg-zinc-800 text-zinc-400" : "bg-gray-100 text-gray-600"}`}
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {playerCount}
          </span>
        )}
      </div>
      <div
        className="flex w-full flex-col md:flex-row justify-evenly items-center gap-3 py-2 mb-4"
      >
        <span className="-rotate-1 rounded-xl border-[3px] border-ink bg-cream px-4 py-1.5 text-center font-serif text-lg font-bold text-ink text-wrap shadow-board-sm">
          {startTitle}
        </span>
        <svg
          className={`w-4 h-4 ${theme.text} shrink-0 transition-transform duration-300 transform rotate-90 md:rotate-0`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M17 8l4 4m0 0l-4 4m4-4H3"
          />
        </svg>
        <span className="rotate-1 rounded-xl border-[3px] border-ink bg-gold px-4 py-1.5 text-center font-serif text-lg font-bold text-ink text-wrap shadow-board-sm">
          {targetTitle}
        </span>
      </div>
      <div
        className={`flex items-center gap-2 ${theme.text} font-medium text-sm`}
      >
        {phase === "Game" && !hasPlayed ? (
          <>
            {gameRemaining != null
              ? `Join Now — ${formatTime(gameRemaining)} remaining`
              : "Join Now"}
          </>
        ) : (
          <>
            {scoreCountdown != null
              ? `New round in ${formatTime(scoreCountdown)}`
              : "Next round starting soon..."}
          </>
        )}
        {phase === "Game" && !hasPlayed && (
          <SvgIcon
            markup={chevronRightIcon}
            size={16}
            className="transition-transform group-hover:translate-x-1"
          />
        )}
      </div>
    </button>
  );
}
